/**
 * ConfirmDialog
 *
 * The portal had a hand-rolled "are you sure?" dialog on most delete buttons, each slightly
 * different. This is the shared one: title, message, a confirm button that can be marked
 * destructive, and a `loading` flag that disables both buttons while the mutation is in flight.
 *
 * Anything richer than a sentence (a list of what will be deleted, a warning Alert) goes in
 * `children`, which renders under the message.
 */

import { ReactNode } from 'react'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material'

export interface ConfirmDialogProps {
  open: boolean
  title: string
  /** The question itself. Omit and use `children` when the body needs more than a sentence. */
  message?: ReactNode
  /** Extra body content rendered below `message`. */
  children?: ReactNode
  /** Default: "Confirm". */
  confirmLabel?: string
  /** Default: "Cancel". */
  cancelLabel?: string
  /** Colours the confirm button `error` — use for anything that deletes or revokes. */
  destructive?: boolean
  /** Disables both buttons and shows a spinner on confirm. */
  loading?: boolean
  onConfirm: () => void
  onClose: () => void
}

export function ConfirmDialog({
  open,
  title,
  message,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  destructive = false,
  loading = false,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  return (
    <Dialog
      open={open}
      // Ignore the backdrop/escape close while the action is running, so a half-finished
      // mutation cannot lose its dialog.
      onClose={loading ? undefined : onClose}
      // Deliberately NOT full-screen on a phone: a one-sentence question taking over the whole
      // screen reads as a new page, and the buttons end up a thumb-stretch from the text.
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { mx: { xs: 2, sm: 4 }, width: { xs: 'calc(100% - 32px)', sm: undefined } } }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {message && <DialogContentText>{message}</DialogContentText>}
        {children}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onClose} disabled={loading}>
          {cancelLabel}
        </Button>
        <Button
          onClick={onConfirm}
          disabled={loading}
          variant="contained"
          color={destructive ? 'error' : 'primary'}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : undefined}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
